import { useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { RichText } from '@mutiny-pkg/champaign-schemas/src/schemas/richText/interfaces';
import { FlexibleTextContent } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { isRichTextEqual, toEditorState, toRichText } from '../ast';
import { isFlexibleTextEqual, flexibleTextToEditorState, editorStateToFlexibleText } from '../flexibleTextAst';
import { isDeepEqual } from '@mutiny-pkg/bin-ui/src/utils/objects';
import { $getSelection, $isRangeSelection } from 'lexical';

export type ExternalStateSynchronizerPluginProps = {
  value: FlexibleTextContent | RichText | string | undefined;
};

export const EXTERNAL_STATE_SYNCHRONIZER_TAG = 'external-state-synchronizer';

export const ExternalStateSynchronizerPlugin = ({ value }: ExternalStateSynchronizerPluginProps) => {
  const [editor] = useLexicalComposerContext();
  const renderedValue = useRef<typeof value | null>(value);

  useEffect(() => {
    if (value === undefined) {
      return;
    }

    if (isDeepEqual(value, renderedValue.current)) {
      return;
    }

    renderedValue.current = value;
    // If the input is focused, we don't want to update the value
    const hasSelection = editor.read(() => {
      const selection = $getSelection();
      return $isRangeSelection(selection);
    });

    if (hasSelection) {
      return;
    }

    const isFlexible = Array.isArray(value);
    let hasChanged: boolean;
    if (isFlexible) {
      hasChanged = !isFlexibleTextEqual(value, editorStateToFlexibleText(editor.getEditorState()));
    } else {
      hasChanged = !isRichTextEqual(value as RichText | string, toRichText(editor.getEditorState()));
    }

    if (hasChanged) {
      const serializedState = isFlexible ? flexibleTextToEditorState(value) : toEditorState(value as RichText | string);
      const parsedState = editor.parseEditorState(serializedState);
      editor.setEditorState(parsedState, { tag: EXTERNAL_STATE_SYNCHRONIZER_TAG });
      // There is a bug where if we call setEditorState without marking all nodes dirty all the transform/
      // would not run. The issue is tracked (though closed) at https://github.com/facebook/lexical/issues/3200
      //
      // If this plugin is used to control the input, we should probably move towards uncontrolled version
      // as it seems like Lexical is not officially supporting this type of use cases. (Most controlled used
      // cases should probably be able to do with node transforms callbacks.)
      editor.update(
        () => {
          const nodeMap = editor.getEditorState()._nodeMap;
          for (const [, node] of nodeMap) {
            node.markDirty();
          }
        },
        { tag: EXTERNAL_STATE_SYNCHRONIZER_TAG },
      );
    }
  }, [value, editor]);

  return null;
};
